import type { ReactNode } from "react";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { isRecord } from "@/apps/admin/utilities/typeGuard";
import type { FailureText as NotificationPayload } from "@/state/api/requestFailure";

export const SEVERITY = {
  ERROR: "error",
  INFO: "info",
  SUCCESS: "success",
  WARNING: "warning",
} as const;

export type Severity = (typeof SEVERITY)[keyof typeof SEVERITY];

export type Notification = {
  title?: string;
  // A string: no caller passes a node, and the error toasts queue one per message
  message?: string;
  debug?: string;
  severity?: Severity;
  autoHide?: boolean;
  closable?: boolean;
  action?: ReactNode;
};

export type NotificationsState = {
  notifications: Notification[];
};

// The error toasts take what callers have: a `{ message, debug }` payload, an Error, an RTK
// error, or nothing. Only a string message or debug line is shown.
const readPayload = (payload: unknown): NotificationPayload =>
  isRecord(payload)
    ? {
        message:
          typeof payload.message === "string" ? payload.message : undefined,
        debug: typeof payload.debug === "string" ? payload.debug : undefined,
      }
    : {};

const initialState: NotificationsState = {
  notifications: [],
};

export const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (
      state,
      { payload: notification }: PayloadAction<Notification>
    ) => {
      state.notifications = [...state.notifications, notification];
    },
    removeNotification: (state) => {
      const [, ...notifications] = state.notifications;
      state.notifications = notifications;
    },
    // Also reached by shared code: state/hooks/useClassification dispatches layout's
    // `notifications/serverErrorNotification` (state/slices/notificationSlice), and this slice,
    // mounted as `notifications` too, handles the same action type. Its payload is a plain
    // `{ message }` (never an Error: actions stay serialisable), which is what this reads.
    serverErrorNotification: (state, action: PayloadAction<unknown>) => {
      const payload = readPayload(action.payload);
      const notification: Notification = {
        title: "An error occured",
        message: payload.message ?? "An unexpected error occured, we're on it.",
        debug: payload.debug,
        severity: SEVERITY.ERROR,
      };
      // Parallel requests failing the same way (e.g. offline) queue one toast, not one each
      const isQueued = state.notifications.some(
        ({ title, message }) =>
          title === notification.title && message === notification.message
      );
      if (!isQueued) {
        state.notifications = [...state.notifications, notification];
      }
    },
    accessDeniedNotification: (state, action: PayloadAction<unknown>) => {
      const payload = readPayload(action.payload);
      state.notifications = [
        ...state.notifications,
        {
          title: "Access Denied",
          message:
            payload.message ?? "You are not authorized to perform this action.",
          debug: payload.debug,
          severity: SEVERITY.ERROR,
        },
      ];
    },
  },
});

export const {
  addNotification,
  removeNotification,
  serverErrorNotification,
  accessDeniedNotification,
} = notificationSlice.actions;

export const selectNotification = (state: {
  notifications: NotificationsState;
}): Notification | null => state.notifications.notifications[0] ?? null;

export default notificationSlice.reducer;
